import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase-client';

interface UserProfile {
  id: string;
  full_name: string;
  profile_photo_url?: string;
  resume_url?: string;
  bio?: string;
  is_recruiter: boolean;
  created_at: string;
  updated_at: string;
}

interface OrganizationData {
  id: string;
  name: string;
  email_domains: string[];
  logo_url?: string;
  description?: string;
}

interface OrganizationMember {
  id: string;
  user_id: string;
  organization_id: string;
  role: string;
  joined_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userProfile: UserProfile | null;
  organizationData: OrganizationData | null;
  organizationMember: OrganizationMember | null;
  isRecruiter: () => boolean;
  getOrganizationId: () => string | null;
  getOrganizationName: () => string | null;
  isRecruiterValue: boolean;
  organizationId: string | null;
  organizationName: string | null;
  signOut: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<{ data?: any; error?: any }>;
  signInWithGoogle: (redirectUrl?: string) => Promise<{ error?: any }>;
  signUp: (email: string, password: string, metadata?: any) => Promise<{ data?: any; error?: any }>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [signOutCount, setSignOutCount] = useState(0);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);
  const [organizationMember, setOrganizationMember] = useState<OrganizationMember | null>(null);

  const fetchUserProfile = async (userId: string) => {
    console.log('[AuthContext] 👤 fetchUserProfile called for:', userId);
    console.log('[AuthContext] 🔧 Supabase client check:', {
      hasSupabase: !!supabase,
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL?.substring(0, 30) + '...',
      hasAnonKey: !!import.meta.env.VITE_SUPABASE_ANON_KEY
    });
    try {
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout')), 5000);
      });

      const fetchPromise = supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      console.log('[AuthContext] 🔄 Starting profile fetch with 5s timeout...');
      const { data: profile, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;

      console.log('[AuthContext] 📋 Profile fetch result:', {
        hasProfile: !!profile,
        hasError: !!error,
        isRecruiter: profile?.is_recruiter,
        profileData: profile
      });

      if (error) {
        console.error('[AuthContext] ❌ Error fetching user profile:', error);
        return;
      }

      if (profile) {
        console.log('[AuthContext] ✅ Setting user profile');
        setUserProfile(profile as UserProfile);

        if (profile.is_recruiter) {
          console.log('[AuthContext] 🏢 User is recruiter, fetching organization...');
          const { data: memberData, error: memberError } = await supabase
            .from('organization_members')
            .select('*, organizations(*)')
            .eq('user_id', userId)
            .maybeSingle();

          console.log('[AuthContext] 🏢 Organization fetch result:', {
            hasMember: !!memberData,
            hasError: !!memberError
          });

          if (memberError) {
            console.error('[AuthContext] ❌ Error fetching organization member:', memberError);
            return;
          }

          if (memberData) {
            console.log('[AuthContext] ✅ Setting organization data');
            setOrganizationMember({
              id: memberData.id,
              user_id: memberData.user_id,
              organization_id: memberData.organization_id,
              role: memberData.role,
              joined_at: memberData.joined_at
            });

            if (memberData.organizations) {
              setOrganizationData(memberData.organizations as OrganizationData);
            }
          }
        }
      }
      console.log('[AuthContext] ✅ fetchUserProfile completed');
    } catch (error: any) {
      console.error('[AuthContext] ❌ Exception fetching user profile:', {
        message: error?.message,
        name: error?.name,
        stack: error?.stack
      });
      if (error?.message === 'Profile fetch timeout') {
        console.error('[AuthContext] ⏱️ Query timed out after 5 seconds - possible network or RLS issue');
      }
    }
  };

  const refreshUserProfile = async () => {
    if (user?.id) {
      await fetchUserProfile(user.id);
    }
  };

  const isRecruiter = useCallback(() => {
    return userProfile?.is_recruiter ?? false;
  }, [userProfile?.is_recruiter]);

  const getOrganizationId = useCallback(() => {
    return organizationMember?.organization_id ?? null;
  }, [organizationMember?.organization_id]);

  const getOrganizationName = useCallback(() => {
    return organizationData?.name ?? null;
  }, [organizationData?.name]);

  useEffect(() => {
    console.log('[AuthContext] 🔄 Initial mount - starting session check');
    let initialLoadResolved = false;

    const resolveInitialLoad = async (session: Session | null, source: string) => {
      if (initialLoadResolved) {
        console.log(`[AuthContext] ⚠️ Initial load already resolved, ignoring ${source}`);
        return;
      }
      initialLoadResolved = true;
      console.log(`[AuthContext] ✅ Resolving initial load via ${source}`);

      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        await fetchUserProfile(session.user.id);
      }

      setLoading(false);
    };

    const getInitialSession = async () => {
      try {
        console.log('[AuthContext] 📡 Starting getSession (2s timeout)...');
        const timeoutId = setTimeout(() => {
          console.warn('[AuthContext] ⏱️ getSession timeout - relying on auth listener');
          resolveInitialLoad(null, 'timeout');
        }, 2000);

        const { data: { session }, error } = await supabase.auth.getSession();
        clearTimeout(timeoutId);

        if (error) {
          console.error('[AuthContext] ❌ getSession error:', error);
        }

        console.log('[AuthContext] 📦 getSession completed, resolving...');
        await resolveInitialLoad(session, 'getSession');
      } catch (error: any) {
        console.error('[AuthContext] ❌ getSession exception:', error.message);
        await resolveInitialLoad(null, 'error');
      }
    };

    getInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('[AuthContext] 🔔 Auth state changed:', {
          event,
          hasSession: !!session,
          userId: session?.user?.id,
          initialLoadResolved
        });

        if (!initialLoadResolved && session) {
          console.log('[AuthContext] 🚀 Fast-track: using auth listener for initial load');
          await resolveInitialLoad(session, 'authStateChange');
          return;
        }

        if (event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] ✅ Token refreshed successfully');
          setSignOutCount(0);
        }

        if (event === 'SIGNED_OUT') {
          setSignOutCount(prev => {
            const newCount = prev + 1;
            if (newCount > 3) {
              console.error('[AuthContext] Too many sign-outs detected, clearing all storage');
              localStorage.clear();
              sessionStorage.clear();
              document.cookie.split(";").forEach((c) => {
                document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
              });
              window.location.href = '/login';
              return 0;
            }
            return newCount;
          });

          console.log('[AuthContext] User signed out, clearing state');
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setOrganizationData(null);
          setOrganizationMember(null);
        } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          console.log('[AuthContext] ✅ Updating session in state');
          setSession(session);
          setUser(session?.user ?? null);
          setSignOutCount(0);

          if (session?.user) {
            await fetchUserProfile(session.user.id);
          }
        } else {
          setSession(session);
          setUser(session?.user ?? null);

          if (session?.user) {
            await fetchUserProfile(session.user.id);
          }
        }

        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signInWithEmail = async (email: string, password: string, retryCount = 0): Promise<{ data?: any; error?: any }> => {
    const MAX_RETRIES = 2;

    try {
      console.log('[AuthContext] Calling signInWithPassword...', retryCount > 0 ? `(retry ${retryCount}/${MAX_RETRIES})` : '');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        clearTimeout(timeoutId);

        console.log('[AuthContext] Sign-in response:', {
          hasData: !!data,
          hasSession: !!data?.session,
          hasUser: !!data?.user,
          error: error?.message
        });

        if (data.session) {
          console.log('[AuthContext] Setting session in context');
          setSession(data.session);
          setUser(data.user);
        }

        return { data, error };
      } catch (fetchError: any) {
        clearTimeout(timeoutId);

        if (fetchError.name === 'AbortError' || fetchError.message?.includes('fetch')) {
          console.warn('[AuthContext] Network error during sign-in:', fetchError.message);

          if (retryCount < MAX_RETRIES) {
            console.log('[AuthContext] Retrying sign-in...');
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
            return signInWithEmail(email, password, retryCount + 1);
          }

          return {
            error: {
              message: 'Network connection failed. Please check your internet connection and try again.'
            }
          };
        }

        throw fetchError;
      }
    } catch (error: any) {
      console.error('[AuthContext] Sign-in exception:', error);
      return {
        error: {
          message: error?.message || 'An unexpected error occurred during sign-in'
        }
      };
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      console.log('[AuthContext] Starting signup for:', email);
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      console.log('[AuthContext] Signup response:', {
        hasData: !!data,
        hasUser: !!data?.user,
        userId: data?.user?.id,
        hasSession: !!data?.session,
        error: error?.message
      });

      if (error) {
        console.error('[AuthContext] Signup error:', error);
      }

      return { data, error };
    } catch (error) {
      console.error('[AuthContext] Signup exception:', error);
      return { data: null, error };
    }
  };

  const signInWithGoogle = async (redirectUrl?: string) => {
    try {
      console.log('[AuthContext] Starting Google sign-in...');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl || `${window.location.origin}/`,
        },
      });

      if (error) {
        console.error('[AuthContext] Google sign-in error:', error);
      }

      return { error };
    } catch (error) {
      console.error('[AuthContext] Google sign-in exception:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      userProfile,
      organizationData,
      organizationMember,
      isRecruiter,
      getOrganizationId,
      getOrganizationName,
      isRecruiterValue: userProfile?.is_recruiter ?? false,
      organizationId: organizationMember?.organization_id ?? null,
      organizationName: organizationData?.name ?? null,
      signOut,
      signInWithEmail,
      signInWithGoogle,
      signUp,
      refreshUserProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
